/**
 * Low level helpers around `<img>` / `<video>` readiness.
 *
 * The flicker this project exists to avoid comes from making an element visible
 * before the browser has a decoded frame for it. Everything here exists to give
 * the caller a promise that only settles once there is something real to show.
 */

/** Below this, seeking costs more than it corrects. */
const SEEK_THRESHOLD_MS = 300

interface VideoFrameCallbackHost {
  requestVideoFrameCallback?: (callback: () => void) => number
  cancelVideoFrameCallback?: (handle: number) => void
}

export function mediaErrorMessage(element: HTMLMediaElement): string {
  const error = element.error
  if (!error) return 'Unknown media error'
  const codes: Record<number, string> = {
    1: 'Loading aborted',
    2: 'Network error',
    3: 'Decode error',
    4: 'Source not supported',
  }
  const label = codes[error.code] ?? `Media error ${error.code}`
  return error.message ? `${label}: ${error.message}` : label
}

function waitForMediaEvent(
  element: HTMLMediaElement,
  event: 'loadeddata' | 'seeked' | 'canplay',
  timeoutMs: number,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false
    const cleanup = () => {
      settled = true
      window.clearTimeout(timer)
      element.removeEventListener(event, onSuccess)
      element.removeEventListener('error', onError)
    }
    const onSuccess = () => {
      if (settled) return
      cleanup()
      resolve()
    }
    const onError = () => {
      if (settled) return
      cleanup()
      reject(new Error(mediaErrorMessage(element)))
    }
    const timer = window.setTimeout(() => {
      if (settled) return
      cleanup()
      reject(new Error(`Timed out after ${timeoutMs}ms waiting for "${event}"`))
    }, timeoutMs)

    element.addEventListener(event, onSuccess)
    element.addEventListener('error', onError)
  })
}

export interface PrepareVideoOptions {
  src: string
  seekMs: number
  loop: boolean
  muted: boolean
  poster: string | null
  timeoutMs: number
}

/**
 * Loads a video and leaves it paused with a decoded frame ready, so that
 * revealing it later is a pure compositor operation.
 */
export async function prepareVideo(
  video: HTMLVideoElement,
  options: PrepareVideoOptions,
): Promise<void> {
  video.muted = options.muted
  video.defaultMuted = options.muted
  video.loop = options.loop
  video.playsInline = true
  video.preload = 'auto'
  video.disableRemotePlayback = true
  if (options.poster) video.poster = options.poster
  else video.removeAttribute('poster')

  const isSameSource = video.src !== '' && new URL(video.src, location.href).href === new URL(options.src, location.href).href

  if (!isSameSource) {
    video.src = options.src
    video.load()
  }
  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    await waitForMediaEvent(video, 'loadeddata', options.timeoutMs)
  }

  const targetSeconds = options.seekMs / 1_000
  const needsSeek =
    options.seekMs > SEEK_THRESHOLD_MS || Math.abs(video.currentTime - targetSeconds) > 0.05
  if (needsSeek) {
    const safeTarget =
      Number.isFinite(video.duration) && video.duration > 0
        ? Math.min(targetSeconds, Math.max(0, video.duration - 0.1))
        : targetSeconds
    video.currentTime = Math.max(0, safeTarget)
    await waitForMediaEvent(video, 'seeked', options.timeoutMs)
  }
}

/** Resolves once the compositor has actually presented a frame (never rejects). */
export function awaitFirstPresentedFrame(video: HTMLVideoElement, timeoutMs: number): Promise<void> {
  const host = video as HTMLVideoElement & VideoFrameCallbackHost
  return new Promise((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      resolve()
    }
    const timer = window.setTimeout(finish, timeoutMs)

    if (typeof host.requestVideoFrameCallback === 'function') {
      host.requestVideoFrameCallback(finish)
    } else {
      requestAnimationFrame(() => requestAnimationFrame(finish))
    }
  })
}

export async function decodeImage(
  image: HTMLImageElement,
  src: string,
  timeoutMs: number,
): Promise<void> {
  if (image.src === '' || new URL(image.src, location.href).href !== new URL(src, location.href).href) {
    image.src = src
  }
  image.decoding = 'sync'

  if (typeof image.decode === 'function') {
    await withTimeout(image.decode(), timeoutMs, `Timed out after ${timeoutMs}ms decoding image`)
    return
  }

  if (image.complete && image.naturalWidth > 0) return

  await new Promise<void>((resolve, reject) => {
    let settled = false
    const cleanup = () => {
      settled = true
      window.clearTimeout(timer)
      image.removeEventListener('load', onLoad)
      image.removeEventListener('error', onError)
    }
    const onLoad = () => {
      if (settled) return
      cleanup()
      resolve()
    }
    const onError = () => {
      if (settled) return
      cleanup()
      reject(new Error('Image failed to load'))
    }
    const timer = window.setTimeout(() => {
      if (settled) return
      cleanup()
      reject(new Error(`Timed out after ${timeoutMs}ms loading image`))
    }, timeoutMs)

    image.addEventListener('load', onLoad)
    image.addEventListener('error', onError)
  })
}

export function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), timeoutMs)
    promise.then(
      (value) => {
        window.clearTimeout(timer)
        resolve(value)
      },
      (error: unknown) => {
        window.clearTimeout(timer)
        reject(error instanceof Error ? error : new Error(String(error)))
      },
    )
  })
}

/** Frees decoder buffers so a long running screen does not accumulate them. */
export function releaseVideo(video: HTMLVideoElement): void {
  video.pause()
  video.removeAttribute('src')
  video.removeAttribute('poster')
  video.load()
}
